// MARK://dhk8/pos/lib/clients/pricing
// Typed client for dhk8-catalog-pricing — reconciled against the published contract
// at services/dhk8-catalog-pricing/src/contract.ts (commit 7349ba2).
//
// Routes are still /api/... (deployed); TODO(reconcile) to migrate to /v1/ with the
// ingress. Envelope: { ok, data } | bare T.
//
// The key endpoint for the POS is POST /api/pricing/calculate — given line inputs
// {productId, quantity, basePriceCents, …} + context {branchId, channel, at}, it
// returns the breakdown after price-list overrides, happy-hour, and promotions. The
// `basePriceCents` is supplied by the caller (it lives in the products cluster).

import { apiFetch, getTenantId, svcUrl } from '../api.js'

// dhk8-catalog-pricing expone /v1/pricing/calculate y /v1/price-lists.
// Ingress route: /v1/dhk8-catalog-pricing/v1/...
const BASE = svcUrl('dhk8-catalog-pricing', '/v1')

type Envelope<T> = { ok: true; data: T } | { ok: false; error: { code: string; message: string } }
async function fetchData<T>(url: string, init?: Parameters<typeof apiFetch>[1]): Promise<T> {
  const body = await apiFetch<Envelope<T> | T>(url, init)
  if (body !== null && typeof body === 'object' && 'ok' in body) {
    const env = body as Envelope<T>
    if (env.ok) return env.data
    throw new Error(`${env.error.code}: ${env.error.message}`)
  }
  return body as T
}

// ── DTOs (mirror of services/dhk8-catalog-pricing/src/contract.ts) ──────────────

export type PricingChannel = 'ALL' | 'DINE_IN' | 'TAKEOUT' | 'DELIVERY' | 'BAR'

export interface CalcLineInput {
  productId: string
  quantity: number
  basePriceCents: number
  categoryId?: string
}

export interface CalcLineResult {
  productId: string
  quantity: number
  basePriceCents: number
  listPriceCents: number
  happyHourDiscountCents: number
  promoDiscountCents: number
  unitPriceCents: number
  subtotalCents: number
  taxCents: number
  totalCents: number
  appliedListId: string | null
  appliedHappyHourId: string | null
  appliedPromoIds: string[]
  reasons: string[]
}

export interface CalcResult {
  subtotalCents: number
  discountsCents: number
  taxCents: number
  totalCents: number
  breakdown: CalcLineResult[]
}

export interface CalculatePricingInput {
  /** UUID; required by the server zod (body). Defaults to getTenantId(). */
  tenantId?: string
  branchId: string
  customerId?: string
  channel?: PricingChannel
  /** ISO timestamp; defaults to "now" on the server. */
  datetime?: string
  items: CalcLineInput[]
}

/** POST /api/pricing/calculate — resolve effective prices for a set of lines. */
export async function calculatePricing(input: CalculatePricingInput, signal?: AbortSignal): Promise<CalcResult> {
  // The server zod (CalculatePricingSchema) parses req.body ONLY and requires
  // tenantId (uuid) + items[] in the body. No query params for /pricing/calculate.
  const tenantId = input.tenantId ?? getTenantId()
  const body = { ...input, tenantId }
  return fetchData<CalcResult>(`${BASE}/pricing/calculate`, { method: 'POST', body, signal })
}

/** Resolve the effective unit price for a single line (best-effort; on failure
 *  returns the supplied base price unchanged). */
export async function resolveUnitPrice(input: { productId: string; basePriceCents: number; quantity?: number; branchId: string; channel?: PricingChannel; categoryId?: string }): Promise<number> {
  try {
    const r = await calculatePricing({
      branchId: input.branchId,
      ...(input.channel ? { channel: input.channel } : {}),
      items: [{ productId: input.productId, quantity: input.quantity ?? 1, basePriceCents: input.basePriceCents, ...(input.categoryId ? { categoryId: input.categoryId } : {}) }],
    })
    return r.breakdown[0]?.unitPriceCents ?? input.basePriceCents
  } catch {
    return input.basePriceCents
  }
}
